import { PropTypes } from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { toggleChatbotVisibility } from '../../redux/actions/chatbot';
import chatAriesIcon from '../../assets/chatAries.png';
import './FloatingChatButton.scss';

// time in milliseconds to show the floating button after user stops scrolling or touching
const FLOATING_BUTTON_DELAY = 5000; // 5 seconds delay
const MOBILE_BREAKPOINT = 768; // max-width for mobile view

class FloatingChatButton extends Component {
  state = {
    showFloatingButton: true,
    isMobile: false,
  };

  scrollTimer = null;
  touchTimer = null;

  componentDidMount() {
    // Check if mobile on mount
    this.checkIfMobile();
    
    // Add resize listener to detect viewport changes
    globalThis.addEventListener('resize', this.checkIfMobile);
    
    // Add movement listeners
    globalThis.addEventListener('scroll', this.handleScroll, { passive: true });
    globalThis.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    globalThis.addEventListener('touchmove', this.handleTouchMove, { passive: true });
    globalThis.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    globalThis.addEventListener('click', this.handleClick, { passive: true });
  }

  componentWillUnmount() {
    // Remove all listeners
    globalThis.removeEventListener('resize', this.checkIfMobile);
    globalThis.removeEventListener('scroll', this.handleScroll);
    globalThis.removeEventListener('touchstart', this.handleTouchStart);
    globalThis.removeEventListener('touchmove', this.handleTouchMove);
    globalThis.removeEventListener('touchend', this.handleTouchEnd);
    globalThis.removeEventListener('click', this.handleClick);

    // Flush timers
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
    }
    if (this.touchTimer) {
      clearTimeout(this.touchTimer);
    }
  }

  checkIfMobile = () => {
    const isMobile = globalThis.innerWidth <= MOBILE_BREAKPOINT;
    this.setState({ isMobile });
    
    // If switching to desktop, ensure button is visible
    if (!isMobile) {
      this.setState({ showFloatingButton: true });
    }
  };

  handleScroll = () => {
    // Only apply hiding logic on mobile
    if (!this.state.isMobile) return;
    
    // Hide the button immediately when scrolling
    this.setState({ showFloatingButton: false });
    
    // Clear existing timer
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
    }
    
    // Show button after FLOATING_BUTTON_DELAY seconds of no scrolling
    this.scrollTimer = setTimeout(() => {
      this.setState({ showFloatingButton: true });
    }, FLOATING_BUTTON_DELAY);
  };

  isFloatingElement = (target) => {
    return (
      target.closest('.floating-chat-button') ||
      target.closest('.fab-container') ||
      target.closest('.fab-item') ||
      target.closest('.add-float_btn')
    );
  };

  handleTouchStart = (e) => {
    // Only apply hiding logic on mobile
    if (!this.state.isMobile) return;
    
    // Don't hide if touching floating elements
    if (this.isFloatingElement(e.target)) return;
    
    // Hide button when touch starts
    this.setState({ showFloatingButton: false });
    
    // Clear existing timer
    if (this.touchTimer) {
      clearTimeout(this.touchTimer);
    }
  };

  handleTouchMove = (e) => {
    // Only apply hiding logic on mobile
    if (!this.state.isMobile) return;
    
    // Don't hide if touching floating elements
    if (this.isFloatingElement(e.target)) return;
    
    // Keep button hidden during touch movement
    this.setState({ showFloatingButton: false });
    
    // Clear existing timer
    if (this.touchTimer) {
      clearTimeout(this.touchTimer);
    }
  };

  handleTouchEnd = (e) => {
    // Only apply hiding logic on mobile
    if (!this.state.isMobile) return;
    
    // Don't start timer if touching floating elements
    if (this.isFloatingElement(e.target)) return;
    
    // Show button after FLOATING_BUTTON_DELAY seconds of no touch activity
    this.touchTimer = setTimeout(() => {
      this.setState({ showFloatingButton: true });
    }, FLOATING_BUTTON_DELAY);
  };

  handleClick = (e) => {
    // Only apply hiding logic on mobile
    if (!this.state.isMobile) return;
    
    // Don't hide if clicking floating elements
    if (this.isFloatingElement(e.target)) return;

    // Hide button when clicking anywhere else
    this.setState({ showFloatingButton: false });
    
    // Clear existing timer
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
    }
    
    // Show button after FLOATING_BUTTON_DELAY seconds
    this.scrollTimer = setTimeout(() => {
      this.setState({ showFloatingButton: true });
    }, FLOATING_BUTTON_DELAY);
  };

  render() {
    const { isVisible, toggleVisibility } = this.props;
    const { showFloatingButton } = this.state;

    return (
      <button
        type="button"
        className="floating-chat-button"
        onClick={toggleVisibility}
        aria-label={isVisible ? 'Close chat' : 'Open chat'}
        style={{
          transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
          opacity: showFloatingButton ? 1 : 0,
          pointerEvents: showFloatingButton ? 'auto' : 'none', // Prevent clicks when hidden
        }}
      >
        {isVisible ? (
          <i className="material-icons" aria-hidden="true">
            close
          </i>
        ) : (
          <img
            src={chatAriesIcon}
            alt="Chat Aries"
            aria-hidden="true"
            className="chat-icon"
          />
        )}
      </button>
    );
  }
}

FloatingChatButton.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  toggleVisibility: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  isVisible: state.chatbot.isVisible,
});

const mapDispatchToProps = {
  toggleVisibility: toggleChatbotVisibility,
};

export default connect(mapStateToProps, mapDispatchToProps)(FloatingChatButton);
